/**
 * @fileoverview Photos Modal with Right-Side Drawer
 *
 * Full-featured photos modal with:
 * - Right-side drawer layout
 * - Project parts filter chips
 * - 3 view modes: Grid, Timeline, Monthly
 * - Integrated timeline views (no separate modals)
 *
 * @module RenovationProject/Components/PhotosModal
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { X, CheckCircle, Circle, SquaresFour, ListBullets, CalendarDots } from '@phosphor-icons/react';
import { FileLog } from 'shared/types/file';
import { UnifiedPhotosGrid } from '../customSections/UnifiedPhotosGrid';
import { PhotosTimelineOverview } from './PhotosTimelineOverview';
import { PhotosTimelineByMonth } from './PhotosTimelineByMonth';

interface ProjectPart {
  id: number;
  title: string;
  status: string;
}

type ViewMode = 'grid' | 'timeline' | 'monthly';

// Special ID to represent photos without a linked project part
const NO_PROJECT_PART_ID = -1;

interface PhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

export const PhotosModal: React.FC<PhotosModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const { session } = useSession();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [projectParts, setProjectParts] = useState<ProjectPart[]>([]);
  const [selectedPartIds, setSelectedPartIds] = useState<number[]>([]);
  const [allPhotos, setAllPhotos] = useState<FileLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch project parts
  useEffect(() => {
    const fetchProjectParts = async () => {
      if (!session || !projectId) {
        if (!isOpen) return; // Don't show errors when modal is closed
        console.log('[PhotosModal] Waiting for session or projectId');
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/project/${projectId}/parts`,
          { method: 'GET' },
          session
        );

        if (response.ok) {
          const parts = await response.json();
          setProjectParts(parts);
          // Select all parts by default, including photos without project part
          setSelectedPartIds([NO_PROJECT_PART_ID, ...parts.map((p: ProjectPart) => p.id)]);
        }
      } catch (error) {
        console.error('Error fetching project parts:', error);
      }
    };

    if (isOpen) {
      fetchProjectParts();
    }
  }, [session, projectId, isOpen]);

  // Fetch all photos
  useEffect(() => {
    const fetchAllPhotos = async () => {
      if (!session || !projectId) {
        if (!isOpen) return; // Don't process when modal is closed
        console.log('[PhotosModal] Waiting for session or projectId for photos');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/renovation-project/${projectId}/images`,
          { method: 'GET' },
          session
        );

        if (response.ok) {
          const photos = await response.json();
          setAllPhotos(photos);
        }
      } catch (error) {
        console.error('Error fetching photos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchAllPhotos();
    }
  }, [session, projectId, isOpen]);

  // Count photos without project part
  const photosWithoutPartCount = useMemo(() => {
    return allPhotos.filter((photo) => (photo as any).projectPartId == null).length;
  }, [allPhotos]);

  // Filter photos by selected parts
  const filteredPhotos = useMemo(() => {
    if (selectedPartIds.length === 0) return [];

    return allPhotos.filter((photo) => {
      const projectPartId = (photo as any).projectPartId;

      // If photo has no project part (null/undefined)
      if (projectPartId == null) {
        return selectedPartIds.includes(NO_PROJECT_PART_ID);
      }

      // If photo has a project part, check if it's selected
      return selectedPartIds.includes(projectPartId);
    });
  }, [allPhotos, selectedPartIds]);

  // Toggle individual part
  const togglePart = (partId: number) => {
    setSelectedPartIds((prev) =>
      prev.includes(partId)
        ? prev.filter((id) => id !== partId)
        : [...prev, partId]
    );
  };

  // Toggle all parts
  const toggleAll = () => {
    const allPartIds = [NO_PROJECT_PART_ID, ...projectParts.map((p) => p.id)];
    if (selectedPartIds.length === allPartIds.length) {
      setSelectedPartIds([]);
    } else {
      setSelectedPartIds(allPartIds);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80">
      {/* Full Page Modal */}
      <div className="relative flex h-full w-full flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-primary/10 bg-white px-6 py-4 shadow-sm">
          <h2 className="text-2xl font-semibold text-neutral-primary">
            Projectfoto's
          </h2>
          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-secondary-mid transition-colors hover:bg-neutral-100 hover:text-neutral-primary"
            aria-label="Sluiten"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        {/* Project Parts Filter */}
        <div className="border-b border-neutral-primary/10 bg-neutral-50 px-6 py-4">
          <div className="container mx-auto max-w-7xl">
            <div className="mb-3 flex items-center gap-3">
              <span className="text-sm font-medium text-neutral-600">
                Filter op onderdeel:
              </span>
              <button
                onClick={toggleAll}
                className="text-sm font-medium text-moss-primary transition-colors hover:text-moss-primary/80"
              >
                {selectedPartIds.length === projectParts.length
                  ? 'Deselecteer alles'
                  : 'Selecteer alles'}
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Special filter for photos without project part */}
              {photosWithoutPartCount > 0 && (
                <button
                  key={NO_PROJECT_PART_ID}
                  onClick={() => togglePart(NO_PROJECT_PART_ID)}
                  className={`flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-medium transition-all ${
                    selectedPartIds.includes(NO_PROJECT_PART_ID)
                      ? 'border-moss-primary bg-moss-primary text-white shadow-sm hover:bg-moss-primary/90'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  {selectedPartIds.includes(NO_PROJECT_PART_ID) ? (
                    <CheckCircle size={16} weight="fill" />
                  ) : (
                    <Circle size={16} />
                  )}
                  <span>Zonder gekoppeld projectonderdeel ({photosWithoutPartCount})</span>
                </button>
              )}

              {/* Regular project parts */}
              {projectParts.map((part) => {
                const isSelected = selectedPartIds.includes(part.id);
                return (
                  <button
                    key={part.id}
                    onClick={() => togglePart(part.id)}
                    className={`flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-medium transition-all ${
                      isSelected
                        ? 'border-moss-primary bg-moss-primary text-white shadow-sm hover:bg-moss-primary/90'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                    }`}
                  >
                    {isSelected ? (
                      <CheckCircle size={16} weight="fill" />
                    ) : (
                      <Circle size={16} />
                    )}
                    <span>{part.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="border-b border-neutral-primary/10 bg-white px-6">
          <div className="container mx-auto flex max-w-7xl">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'border-b-2 border-moss-primary text-moss-primary'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-primary'
              }`}
            >
              <SquaresFour size={18} weight={viewMode === 'grid' ? 'fill' : 'regular'} />
              <span>Alle foto's</span>
            </button>

            <button
              onClick={() => setViewMode('timeline')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
                viewMode === 'timeline'
                  ? 'border-b-2 border-moss-primary text-moss-primary'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-primary'
              }`}
            >
              <ListBullets size={18} weight={viewMode === 'timeline' ? 'fill' : 'regular'} />
              <span>Evolutie per maand</span>
            </button>

            <button
              onClick={() => setViewMode('monthly')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 font-medium transition-colors ${
                viewMode === 'monthly'
                  ? 'border-b-2 border-moss-primary text-moss-primary'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-primary'
              }`}
            >
              <CalendarDots size={18} weight={viewMode === 'monthly' ? 'fill' : 'regular'} />
              <span>Filmstrip weergave</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-neutral-50">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="border-moss-primary h-12 w-12 animate-spin rounded-full border-b-2" />
            </div>
          ) : viewMode === 'grid' ? (
            <div className="container mx-auto max-w-7xl p-6">
              <UnifiedPhotosGrid
                projectId={projectId}
                selectedPartIds={selectedPartIds}
              />
            </div>
          ) : viewMode === 'timeline' ? (
            <div className="container mx-auto max-w-7xl p-6">
              <PhotosTimelineOverview
                photos={filteredPhotos}
                projectParts={projectParts}
              />
            </div>
          ) : (
            <div className="container mx-auto max-w-7xl p-6">
              <PhotosTimelineByMonth photos={filteredPhotos} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
